/**
 * API Core - Base configuration, error handling, and request utilities
 *
 * Includes automatic token refresh on 401 responses.
 */

import { API_BASE_URL } from '../../config/api';
import { storageGet, storageSet, storageRemove } from '../../lib/nativeStorage';
import { isJoinable, singleFlight } from './coalesce';
import { t } from '../../i18n';

// Re-export for use in components
export { API_BASE_URL };

// Token management.
// On web these are populated synchronously from localStorage. On native the
// storage cache is empty until hydrateStorage() runs at boot, so the native
// bootstrap MUST call reloadAuthFromStorage() afterwards to sync these vars.
let authToken: string | null = storageGet('buzzbit_token');
let currentWorkspaceId: string | null = storageGet('buzzbit_workspace');

/**
 * Re-read auth state from persistent storage into the in-memory vars.
 * Call once after hydrateStorage() on native; harmless on web.
 */
export const reloadAuthFromStorage = () => {
  authToken = storageGet('buzzbit_token');
  currentWorkspaceId = storageGet('buzzbit_workspace');
};

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    storageSet('buzzbit_token', token);
  } else {
    storageRemove('buzzbit_token');
  }
};

/**
 * Fired when the active workspace actually changes (including to null on
 * logout). Persisted client stores subscribe so that one account's cached data
 * can never render for the next one on a shared browser — localStorage outlives
 * the session, so a store keyed globally leaks straight across logins.
 */
const workspaceChangeListeners = new Set<() => void>();

export const onWorkspaceChange = (fn: () => void): (() => void) => {
  workspaceChangeListeners.add(fn);
  return () => workspaceChangeListeners.delete(fn);
};

export const setWorkspaceId = (workspaceId: string | null) => {
  const changed = currentWorkspaceId !== workspaceId;
  currentWorkspaceId = workspaceId;
  if (workspaceId) {
    storageSet('buzzbit_workspace', workspaceId);
  } else {
    storageRemove('buzzbit_workspace');
  }
  if (changed) {
    for (const fn of workspaceChangeListeners) {
      // A misbehaving listener must never break login or logout.
      try { fn(); } catch { /* ignore */ }
    }
  }
};

export const getAuthToken = () => {
  if (!authToken) return null;
  try {
    const parts = authToken.split('.');
    if (parts.length !== 3) { clearAuth(); return null; }
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      // Expired is NOT logged out. Drop only the stale access token; the
      // refresh token and workspace survive so the auth gate (or the next
      // apiRequest) can renew the session. Wiping everything here logged
      // users out for parking a tab past the 2h access-token TTL — e.g.
      // reading /terms, then "Back to Home" landed on the login page.
      setAuthToken(null);
      return null;
    }
  } catch {
    clearAuth();
    return null;
  }
  return authToken;
};
export const getWorkspaceId = () => currentWorkspaceId;

/** True when a refresh token is stored — the session is renewable even if the
 *  access token is missing or expired. */
export const hasRefreshToken = (): boolean => !!storageGet('buzzbit_refresh_token');

/** Renew the access token from the stored refresh token (deduped — safe to
 *  call from anywhere). Resolves true when a new access token was installed. */
export const refreshSession = (): Promise<boolean> => tryRefreshToken();

// API Error types
export interface ApiValidationDetail {
  field: string;
  message: string;
  code?: string;
}

export interface ApiError {
  status: number;
  code: string;
  message: string;
  retryAfter?: number;
  requestId?: string;
  /** Per-field Zod validation details for 400 / 422 responses */
  details?: ApiValidationDetail[];
  /** The server's specific refusal (`error.details.code`, e.g. SANDBOX_REQUIRED) when it named one. */
  reason?: string;
}

// Event emitter for API errors
type ErrorHandler = (error: ApiError) => void;
const errorHandlers: Set<ErrorHandler> = new Set();

export const onApiError = (handler: ErrorHandler): (() => void) => {
  errorHandlers.add(handler);
  return () => errorHandlers.delete(handler);
};

const emitError = (error: ApiError) => {
  errorHandlers.forEach(handler => {
    try { handler(error); } catch (e) { console.error('[API] Error handler threw:', e); }
  });
};

// ==================== TOKEN REFRESH ====================

import { backoffKey, heldSeconds, holdFor, isHeldOn429, retryAfterSeconds } from './rateLimitBackoff';

let refreshPromise: Promise<boolean> | null = null;
const REFRESH_KEY = backoffKey('POST', '/auth/refresh-token');

function clearAuth() {
  setAuthToken(null);
  setWorkspaceId(null);
  storageRemove('buzzbit_refresh_token');
}

/**
 * True when the access token is present but within `skewMs` of expiring (or
 * already expired). Lets apiRequest refresh PROACTIVELY, before firing — so a
 * burst of page-load requests doesn't each 401→refresh→retry and spray red
 * errors in the browser console. Malformed/exp-less tokens → false (fall back
 * to the reactive 401 path).
 */
function tokenExpiringWithin(skewMs: number): boolean {
  if (!authToken) return false;
  try {
    const parts = authToken.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 - Date.now() < skewMs;
  } catch {
    return false;
  }
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Uses a shared promise to prevent concurrent refresh requests.
 */
async function tryRefreshToken(): Promise<boolean> {
  // If already refreshing, wait for the same promise
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = storageGet('buzzbit_refresh_token');
    if (!refreshToken) return false;
    // One attempt per server window: the gate calls this on every navigation
    // while a refresh token is stored (see rateLimitBackoff.ts).
    if (heldSeconds(REFRESH_KEY) > 0) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          const hint = retryAfterSeconds(await response.json().catch(() => null), response.headers);
          if (hint !== null) holdFor(REFRESH_KEY, hint);
        } else if (response.status === 401 || response.status === 403) {
          // The server refused THIS token for good (expired, revoked, or
          // already spent — and replaying a spent one revokes every session).
          // Drop it so the gate stops presenting it on every navigation, but
          // only if another tab has not rotated it in the meantime.
          if (storageGet('buzzbit_refresh_token') === refreshToken) {
            storageRemove('buzzbit_refresh_token');
          }
        }
        return false;
      }

      // Unwrap the server envelope: sendSuccess() replies
      // { success: true, data: { token, refreshToken } }. Reading `json.token`
      // here found undefined on EVERY successful refresh, so this returned
      // false and the caller ran clearAuth() — a full logout on the first 401
      // despite a valid 7-day refresh token. Worse, the server had already
      // rotated and revoked the old refresh token by then, so the session was
      // unrecoverable. Login.tsx has always unwrapped; this path did not.
      const json = await response.json();
      const data = (json?.data ?? json) as { token?: string; refreshToken?: string };
      if (data?.token) {
        setAuthToken(data.token);
        if (data.refreshToken) {
          storageSet('buzzbit_refresh_token', data.refreshToken);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// ==================== ERROR HANDLING ====================

function handleHttpError(status: number, errorBody: Record<string, unknown>): ApiError {
  const requestId = errorBody.requestId as string | undefined;

  // Standard BuzzBit API format: { success: false, error: { code, message } }
  // Extract nested error fields so all cases can use them.
  const nested = errorBody.error as Record<string, unknown> | string | undefined;
  const nestedMsg = (nested && typeof nested === 'object') ? nested.message as string | undefined : undefined;
  const nestedCode = (nested && typeof nested === 'object') ? nested.code as string | undefined : undefined;
  // sendError's `details.code` — the specific refusal behind a generic HTTP code.
  const nestedDetails = (nested && typeof nested === 'object') ? nested.details : undefined;
  const reason = (nestedDetails && typeof nestedDetails === 'object' && typeof (nestedDetails as Record<string, unknown>).code === 'string')
    ? ((nestedDetails as Record<string, unknown>).code as string)
    : undefined;
  // Flat fallbacks for non-standard responses
  const flatMsg = typeof errorBody.message === 'string' ? errorBody.message : undefined;
  const errorMessage = nestedMsg || flatMsg;

  // Zod field-level details (flat envelope: { error, code, details: [{field, message, code}] })
  const rawDetails = errorBody.details;
  const details: ApiValidationDetail[] | undefined = Array.isArray(rawDetails)
    ? rawDetails
        .filter((d): d is Record<string, unknown> => typeof d === 'object' && d !== null)
        // `nodeId` is a locator too. Flow activation refusals arrive as
        // `{ nodeId, type, message }` (sendFlowValidationError), so requiring
        // `field` parsed every reason and then dropped it on the floor — the
        // merchant got "Flow has 1 validation error(s)" and no way to learn
        // which one, while the client was holding the answer.
        .map((d) => ({
          field: typeof d.field === 'string' && d.field
            ? d.field
            : (typeof d.nodeId === 'string' ? d.nodeId : ''),
          message: typeof d.message === 'string' ? d.message : '',
          code: typeof d.code === 'string' ? d.code : (typeof d.type === 'string' ? d.type : undefined),
        }))
        // A reason with no locator is still a reason worth showing; only a
        // detail with no MESSAGE is useless.
        .filter((d) => d.message)
    : undefined;

  const mapped = ((): ApiError => {
  switch (status) {
    case 401:
      // Don't clear auth here — let apiRequest handle refresh first
      return { status, code: 'UNAUTHORIZED', message: 'Your session has expired. Please log in again.', requestId };
    case 400:
      // A stable code with a next step the merchant can take reads as the
      // app's own (translated) copy, not as the server's English.
      if (nestedCode === 'WHATSAPP_NOT_CONNECTED') {
        return { status, code: nestedCode, message: t('integrations.whatsappNotConnected'), requestId };
      }
      return { status, code: nestedCode || 'VALIDATION_ERROR', message: errorMessage || 'Please check your input and try again.', requestId, details };
    case 403:
      return { status, code: nestedCode || 'FORBIDDEN', message: errorMessage || 'You do not have permission.', requestId };
    case 404:
      return { status, code: nestedCode || 'NOT_FOUND', message: errorMessage || 'Resource not found.', requestId };
    case 422:
      return { status, code: nestedCode || 'VALIDATION_ERROR', message: errorMessage || 'Invalid data.', requestId, details };
    case 429: {
      const retryAfter = parseInt(errorBody.retryAfter as string || '60', 10);
      return { status, code: 'RATE_LIMITED', message: `Too many requests. Wait ${retryAfter}s.`, retryAfter, requestId };
    }
    case 500: case 502: case 503: case 504: {
      // Envelope is `{ success: false, error: { code, message } }`. Keep
      // `error.message` when present — production often omits `details`, so we
      // must not require `details.code` to surface the server's copy.
      const serverMsg = typeof errorMessage === 'string' ? errorMessage.trim() : '';
      return {
        status,
        code: nestedCode || (status === 503 ? 'SERVICE_UNAVAILABLE' : 'SERVER_ERROR'),
        message: serverMsg,
        requestId,
      };
    }
    default:
      return { status, code: nestedCode || 'UNKNOWN_ERROR', message: errorMessage || `Unexpected error (${status})`, requestId };
  }
  })();
  return reason ? { ...mapped, reason } : mapped;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly retryAfter?: number;
  public readonly requestId?: string;
  public readonly details?: ApiValidationDetail[];
  public readonly reason?: string;
  constructor(apiError: ApiError) {
    super(apiError.message);
    this.name = 'ApiRequestError';
    this.status = apiError.status;
    this.code = apiError.code;
    this.retryAfter = apiError.retryAfter;
    this.requestId = apiError.requestId;
    this.details = apiError.details;
    this.reason = apiError.reason;
  }
}

// ==================== API REQUEST ====================

async function requestOnce<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // The session check still inside a window the server set with a 429:
  // answer with that 429 locally rather than spend budget re-asking. Held per
  // token, like the server's bucket, so one account's window never holds the
  // first check of the next login.
  const requestKey = backoffKey(options.method, endpoint);
  const holdKey = (): string => `${requestKey}|${authToken ?? ''}`;
  const wait = heldSeconds(holdKey());
  if (wait > 0) {
    const held: ApiError = { status: 429, code: 'RATE_LIMITED', message: `Too many requests. Wait ${wait}s.`, retryAfter: wait };
    emitError(held);
    throw new ApiRequestError(held);
  }

  const makeRequest = async (): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    if (currentWorkspaceId) headers['X-Workspace-Id'] = currentWorkspaceId;

    return fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  };

  // Proactive refresh: if the token is about to expire, renew it BEFORE firing
  // so parallel navigation requests don't each hit a 401 (deduped via the
  // shared refreshPromise). The reactive 401 path below still covers tokens
  // that expire unexpectedly.
  const isAuthEndpoint = endpoint.includes('/auth/refresh-token') || endpoint.includes('/auth/login');
  if (authToken && !isAuthEndpoint && tokenExpiringWithin(30_000)) {
    await tryRefreshToken();
  }

  let response: Response;
  try {
    response = await makeRequest();
  } catch (networkError) {
    const aborted =
      (networkError instanceof DOMException && networkError.name === 'AbortError') ||
      (networkError instanceof Error && networkError.name === 'AbortError');
    const error: ApiError = {
      status: 0,
      code: 'NETWORK_ERROR',
      message: aborted
        ? 'Request timed out. If you published, check the calendar before trying again.'
        : 'Unable to connect to server.',
    };
    emitError(error);
    throw new ApiRequestError(error);
  }

  // On 401: try refresh, then retry the original request once
  if (response.status === 401 && !endpoint.includes('/auth/refresh-token') && !endpoint.includes('/auth/login')) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      // Retry the original request with new token
      try {
        response = await makeRequest();
      } catch (networkError) {
        const error: ApiError = { status: 0, code: 'NETWORK_ERROR', message: 'Unable to connect to server.' };
        emitError(error);
        throw new ApiRequestError(error);
      }
    } else {
      // Refresh failed — clear auth and redirect to login
      clearAuth();
      const apiError: ApiError = { status: 401, code: 'UNAUTHORIZED', message: 'Your session has expired. Please log in again.' };
      emitError(apiError);
      throw new ApiRequestError(apiError);
    }
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: 'Request failed' }));
    const apiError = handleHttpError(response.status, errorBody);
    if (response.status === 429) {
      const hint = retryAfterSeconds(errorBody, response.headers);
      if (hint !== null && isHeldOn429(requestKey)) holdFor(holdKey(), hint);
    }
    // If still 401 after refresh attempt, clear auth
    if (response.status === 401) {
      clearAuth();
    }
    emitError(apiError);
    throw new ApiRequestError(apiError);
  }
  return response.json();
}

// ==================== IN-FLIGHT GET COALESCING ====================

/**
 * While an identical GET is on the wire, later callers join it rather than
 * opening a second connection. The mechanism, the reasoning and the safety
 * argument live in ./coalesce.ts; what belongs here is the key, because only
 * this module knows what a response depends on.
 *
 * Token and workspace are in the key so a switch or a refresh mid-flight can
 * never hand one account's response to another. Nothing else needs to be:
 * `isJoinable` refuses any request that carries options beyond `method`, so a
 * joinable request has nothing else that could vary.
 */
function coalesceKey(endpoint: string): string {
  return `${authToken ?? ""}|${currentWorkspaceId ?? ""}|${endpoint}`;
}

export function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  if (!isJoinable(options)) return requestOnce<T>(endpoint, options);
  return singleFlight<T>(coalesceKey(endpoint), () => requestOnce<T>(endpoint, options));
}


// ==================== TOAST INTEGRATION ====================

import { toast } from '../../stores/toastStore';

// Auto-display API errors as toasts
onApiError((error) => {
  if (error.code === 'NETWORK_ERROR') {
    toast.error('Unable to connect to server. Check your connection.');
  } else if (error.status >= 500) {
    // Prefer `error.message` from `{ error: { message } }`; only fall back when
    // the server sent nothing (handleHttpError leaves message empty then).
    const serverMsg = typeof error.message === 'string' ? error.message.trim() : '';
    toast.error(serverMsg || 'Server error. Please try again later.');
  }
  // 401/403 are handled by redirect logic, don't toast those
});
